// Fixed Term Contracts Expiration report (ADR-090)
// Mirrors the Bench Report look & feel: header stats, filters bar, employee
// rows with a dates/urgency cell, cursor-paged Load More, and click-through
// navigation that returns to the report with filters and scroll restored.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useOpenIndividual from '../../../hooks/useOpenIndividual';
import { INDIVIDUAL_ENTRY_POINTS } from '../../../constants/individualOpenModes';
import {
  FaArrowLeft,
  FaSortAmountDown,
  FaSortAmountUp,
} from 'react-icons/fa';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, MainSection, BackButton } from '../styles';
import {
  FiltersBar,
  HeaderStats,
  HeaderStat,
  EmployeeRow,
  EmployeeRowMain,
  EmployeeInfo,
  DaysCell,
  LoadMoreButton,
  SortButton,
} from './styles';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import ResultsCount from '../../common/ResultsCount';
import EmployeeSearch from '../../common/EmployeeSearch';
import { EmployeeSearchProvider } from '../../common/EmployeeSearch/contexts/EmployeeSearchProvider';
import ClusterSelector from '../../common/ClusterSelector';
import BusinessUnitSelector from '../../common/BusinessUnitSelector';
import EmployeeReportingScope from '../../common/filters/EmployeeReportingScope';
import { EMPLOYEE_REPORTING_SCOPE } from '../../../constants/employeeReportingScope';
import { fixedTermContractsApi } from '../../../services/api/fixedTermContractsApi';
import { EXPIRY_WINDOW_OPTIONS, EXPIRY_OVERDUE, formatContractDuration } from '../../../constants/fixedTermContracts';
import { formatContractNumber } from '../../../constants/contractActions';
import { formatDateOnly } from '../../../utils/dateOnly';

// ADR-197: date-only values — the stored calendar day, never a local instant
const formatDate = (dateStr) => formatDateOnly(dateStr);

const DateChip = ({ prefix, date }) => (
  <span style={{
    fontSize: '11px',
    fontWeight: 500,
    color: '#64748B',
    backgroundColor: '#F1F5F9',
    border: '1px solid #E2E8F0',
    borderRadius: '4px',
    padding: '1px 6px',
    whiteSpace: 'nowrap',
  }}>
    {prefix} {formatDate(date)}
  </span>
);

const ContractDatesCell = ({ daysUntilExpiry, overdue, contractStartDate, contractEndDate, contractNumber }) => {
  const duration = formatContractDuration(contractStartDate, contractEndDate);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
      <DaysCell $days={daysUntilExpiry}>
        {overdue
          ? `overdue ${Math.abs(daysUntilExpiry)}d`
          : daysUntilExpiry === 0
            ? 'today'
            : `in ${daysUntilExpiry}d`}
      </DaysCell>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {contractStartDate && <DateChip prefix="from" date={contractStartDate} />}
        {contractEndDate && <DateChip prefix="until" date={contractEndDate} />}
      </div>
      {duration && (
        <span style={{ fontSize: '11px', fontWeight: 500, color: '#94A3B8', whiteSpace: 'nowrap' }}>
          {duration} contract
        </span>
      )}
      {/* ADR-136: the contract's number in the system of record (ADR-049
          sourceReference.contractId). Display-only — not sortable or filterable.
          Rendered only when the episode carries one: historical episodes and
          open-ended hires have none, and an always-present empty slot would just
          be noise. */}
      {contractNumber && (
        <span style={{ fontSize: '11px', fontWeight: 500, color: '#94A3B8', whiteSpace: 'nowrap' }}>
          {formatContractNumber(contractNumber)}
        </span>
      )}
    </div>
  );
};

const FixedTermContractsReport = () => {
  const navigate = useNavigate();
  const openIndividual = useOpenIndividual();
  const location = useLocation();
  const mainSectionRef = useRef(null);
  const scrollRestoredRef = useRef(false);

  // Restore state when returning from employee profile, or when entering from
  // the dashboard card (ADR-091) which seeds the same ftcState shape.
  const restored = location.state?.ftcState;

  // Where the back button goes. The report is reachable from the Reports grid
  // (default) and from the dashboard card, which seeds ftcState.backTo/backLabel
  // so the user returns where they came from.
  //
  // NB: this deliberately does NOT read location.state.returnTo — on this page
  // that key means "where the EMPLOYEE profile should return to" (i.e. back to
  // this report). EmployeeForm returns via navigate(returnTo, { state:
  // location.state }), forwarding the whole object, so reading it here would
  // make the report's own back button point at itself. Keeping the origin
  // inside ftcState also means it survives that round trip.
  const backTo = restored?.backTo || '/reports';
  const backLabel = restored?.backLabel || 'Reports';

  // Filter state — initialized from restored state if available
  const [clusterId, setClusterId] = useState(restored?.clusterId || '');
  const [businessUnitId, setBusinessUnitId] = useState(restored?.businessUnitId || '');
  const [expiresWithinDays, setExpiresWithinDays] = useState(restored?.expiresWithinDays || '');
  const [selectedEmployee, setSelectedEmployee] = useState(restored?.selectedEmployee || null);
  // ADR-092: representative (a manager) + reporting scope of their span.
  // The scope chips are inert until a representative is selected.
  const [representative, setRepresentative] = useState(restored?.representative || null);
  const [representativeScope, setRepresentativeScope] = useState(
    restored?.representativeScope || EMPLOYEE_REPORTING_SCOPE.DIRECT,
  );
  const [orderDirection, setOrderDirection] = useState(restored?.orderDirection || 'asc');

  // Data state
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [employees, setEmployees] = useState([]);

  const fetchReport = useCallback(async (cursor = undefined, append = false) => {
    try {
      if (!append) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      const params = {
        limit: 50,
        orderDirection,
        cursor,
      };

      if (clusterId) {
        params.clusterId = clusterId;
      }
      if (businessUnitId) {
        params.businessUnitId = businessUnitId;
        params.buScope = 'hierarchy';
      }
      // The Overdue option is a distinct predicate, not a window (ADR-090:
      // overdue rows match every numeric window), so it maps to its own param.
      if (expiresWithinDays === EXPIRY_OVERDUE) {
        params.overdueOnly = true;
      } else if (expiresWithinDays) {
        params.expiresWithinDays = expiresWithinDays;
      }
      if (selectedEmployee) {
        params.employeeId = selectedEmployee.id;
      }
      if (representative) {
        params.representativeId = representative.id;
        params.reportingScope = representativeScope;
      }

      const data = await fixedTermContractsApi.getFixedTermContractsReport(params);

      if (append) {
        setEmployees((prev) => [...prev, ...data.employees]);
      } else {
        setEmployees(data.employees);
      }

      setReport(data);
    } catch (error) {
      console.error('Error fetching fixed term contracts report:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [clusterId, businessUnitId, expiresWithinDays, selectedEmployee, representative, representativeScope, orderDirection]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // ADR-092: clearing the representative resets the scope so a future
  // selection starts from the Direct Reports default again.
  const handleRepresentativeChange = useCallback((emp) => {
    setRepresentative(emp);
    if (!emp) {
      setRepresentativeScope(EMPLOYEE_REPORTING_SCOPE.DIRECT);
    }
  }, []);

  const handleLoadMore = () => {
    if (report?.pageInfo?.hasMore && report.pageInfo.cursor) {
      fetchReport(report.pageInfo.cursor, true);
    }
  };

  const toggleSortDirection = () => {
    setOrderDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  // Restore scroll position after data loads on return from employee profile
  useEffect(() => {
    if (!isLoading && restored?.scrollTop && !scrollRestoredRef.current && mainSectionRef.current) {
      mainSectionRef.current.scrollTop = restored.scrollTop;
      scrollRestoredRef.current = true;
    }
  }, [isLoading, restored]);

  const handleNavigateToEmployee = useCallback((emp) => {
    const ftcState = {
      clusterId,
      businessUnitId,
      expiresWithinDays,
      selectedEmployee,
      representative,
      representativeScope,
      orderDirection,
      // Carry the origin through the employee round trip, so returning here
      // keeps pointing back at the dashboard when that's where we came from.
      backTo,
      backLabel,
      scrollTop: mainSectionRef.current?.scrollTop || 0,
    };
    // ADR-109 — auto open (Edit when the user manages the individual)
    openIndividual(emp.employee.id, {
      entryPoint: INDIVIDUAL_ENTRY_POINTS.FIXED_TERM_CONTRACTS,
      returnTo: '/reports/fixed-term-contracts',
      returnLabel: 'Fixed Term Contracts',
      state: { ftcState },
    });
  }, [clusterId, businessUnitId, expiresWithinDays, selectedEmployee, representative, representativeScope, orderDirection, backTo, backLabel, openIndividual]);

  const summary = report?.summary || { totalContracts: 0, overdue: 0, expiring30: 0, expiring60: 0, expiring90: 0 };
  const filteredTotal = report?.pageInfo?.filteredTotal ?? employees.length;

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate(backTo)}>
            <FaArrowLeft /> Back to {backLabel}
          </BackButton>
          <Title>Fixed Term Contracts</Title>
        </HeaderLeft>
        <HeaderStats>
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <>
              <HeaderStat>
                <div className="top">
                  <span className="label">Contracts</span>
                  <span className="value">{summary.totalContracts}</span>
                </div>
              </HeaderStat>
              <HeaderStat>
                <div className="top">
                  <span className="label">&le; 30 Days</span>
                  <span className="value">{summary.expiring30}</span>
                </div>
              </HeaderStat>
              <HeaderStat>
                <div className="top">
                  <span className="label">&le; 60 Days</span>
                  <span className="value">{summary.expiring60}</span>
                </div>
              </HeaderStat>
              <HeaderStat>
                <div className="top">
                  <span className="label">&le; 90 Days</span>
                  <span className="value">{summary.expiring90}</span>
                </div>
              </HeaderStat>
              <HeaderStat>
                <div className="top">
                  <span className="label">Overdue</span>
                  <span className="value">{summary.overdue}</span>
                </div>
                <span className="sub">Past end, unprocessed</span>
              </HeaderStat>
            </>
          )}
        </HeaderStats>
      </Header>

      <Content>
        <MainSection ref={mainSectionRef}>
          {/* Filters */}
          <FiltersBar>
            <ClusterSelector
              value={clusterId}
              onChange={setClusterId}
              label="Cluster"
              showStructureType // ADR-119
              size="small"
              fullWidth={false}
              sx={{ minWidth: 180 }}
            />

            <BusinessUnitSelector
              includeInactive // ADR-120: reports keep ended units, marked "Ended <date>"
              value={businessUnitId}
              onChange={setBusinessUnitId}
              label="Business Unit"
              showStructureType // ADR-119
              size="small"
              fullWidth={false}
              sx={{ minWidth: 180 }}
            />

            <EmployeeSearchProvider instanceId="ftc-report-search">
              <EmployeeSearch
                value={selectedEmployee}
                onChange={setSelectedEmployee}
                label="Individual"
                placeholder="Search individuals..."
                width={260}
                height={40}
                hideLabel
              />
            </EmployeeSearchProvider>

            {/* ADR-092: representative (manager) filter + reporting scope chips */}
            <EmployeeSearchProvider instanceId="ftc-report-representative">
              <EmployeeSearch
                value={representative}
                onChange={handleRepresentativeChange}
                label="Representative"
                placeholder="Search representatives..."
                width={240}
                height={40}
                hideLabel
              />
            </EmployeeSearchProvider>

            <EmployeeReportingScope
              selectedScope={representative ? representativeScope : ''}
              onScopeChange={(scope) => representative && setRepresentativeScope(scope)}
              disabledScopes={[EMPLOYEE_REPORTING_SCOPE.ALL]}
              disabled={!representative}
              labelOverrides={{ [EMPLOYEE_REPORTING_SCOPE.HIERARCHY]: 'Hierarchy' }}
            />

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Expires</InputLabel>
              <Select
                value={expiresWithinDays}
                label="Expires"
                onChange={(e) => setExpiresWithinDays(e.target.value)}
              >
                {EXPIRY_WINDOW_OPTIONS.map((option) => (
                  <MenuItem key={String(option.value)} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FiltersBar>

          {/* Sort control */}
          {!isLoading && employees.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <ResultsCount count={filteredTotal} singular="contract" plural="contracts" />
              <SortButton onClick={toggleSortDirection}>
                {orderDirection === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />}
                Contract end date ({orderDirection === 'asc' ? 'soonest' : 'latest'} first)
              </SortButton>
            </div>
          )}

          {/* Employee list */}
          {isLoading ? (
            <LoadingSpinner />
          ) : employees.length === 0 ? (
            <EmptyState
              title="No Fixed Term Contracts Found"
              description="No contracts match the current filters. Try adjusting your search criteria."
            />
          ) : (
            <>
              {employees.map((emp) => (
                <EmployeeRow key={emp.episodeId}>
                  <EmployeeRowMain>
                    <EmployeeAvatar employee={emp.employee} size={40} />

                    <EmployeeInfo>
                      <div
                        className="name name-link"
                        onClick={() => handleNavigateToEmployee(emp)}
                      >
                        {emp.employee.fullName}
                      </div>
                      <div className="detail">
                        {emp.employee.position?.label || 'No position'}
                        {emp.employee.grade?.label && ` • ${emp.employee.grade.label}`}
                      </div>
                      <div className="detail">
                        {[
                          emp.cluster?.name,
                          emp.businessUnit?.name,
                          emp.employee.contractType?.name,
                          emp.employee.workingLocation?.name,
                          emp.directManager ? `Representative: ${emp.directManager.fullName}` : null,
                        ].filter(Boolean).join(' • ')}
                      </div>
                    </EmployeeInfo>

                    <ContractDatesCell
                      daysUntilExpiry={emp.daysUntilExpiry}
                      overdue={emp.overdue}
                      contractStartDate={emp.contractStartDate}
                      contractEndDate={emp.contractEndDate}
                      contractNumber={emp.contractNumber}
                    />
                  </EmployeeRowMain>
                </EmployeeRow>
              ))}

              {report?.pageInfo?.hasMore && (
                <LoadMoreButton onClick={handleLoadMore} disabled={isLoadingMore}>
                  {isLoadingMore ? (
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                  ) : null}
                  Load More
                </LoadMoreButton>
              )}
            </>
          )}
        </MainSection>
      </Content>
    </Container>
  );
};

export default FixedTermContractsReport;
